import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import {useLanguage} from '../context/LanguageContext';

const {FiCheckCircle} = FiIcons;

export default function Hero() {
  const {t} = useLanguage();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = () => {
    setTimeout(() => {
      setIsSubmitted(true);
    }, 1000);
  };

  return (
    <section className="relative pt-32 pb-16 px-6 md:px-12 max-w-5xl mx-auto flex flex-col items-center text-center">
      <iframe name="hidden_iframe" id="hidden_iframe" style={{display: 'none'}} title="Hidden newsletter submission target"></iframe>

      <motion.div
        key={t.hero.headline}
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        transition={{duration: 0.8, ease: 'easeOut'}}
      >
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-zinc-50 mb-6 leading-[1.05]">
          {t.hero.headline}
        </h1>
        <p className="text-lg md:text-2xl text-zinc-400 mb-12 max-w-2xl mx-auto font-light leading-relaxed">
          {t.hero.subhead}
        </p>
      </motion.div>

      <div className="w-full max-w-xl">
        <AnimatePresence mode="wait">
          {!isSubmitted ? (
            <motion.div
              key="form"
              initial={{opacity: 0, scale: 0.98}}
              animate={{opacity: 1, scale: 1}}
              exit={{opacity: 0, scale: 0.95, y: -10}}
              transition={{duration: 0.5}}
              className="bg-zinc-900/40 border border-zinc-800/50 p-8 rounded-[2rem] backdrop-blur-md shadow-2xl"
            >
              <p className="text-[10px] text-zinc-500 mb-8 font-bold tracking-[0.3em] uppercase">
                {t.hero.optIn}
              </p>

              <form
                action="https://sendfox.com/form/1998yn/198qzx"
                method="post"
                target="hidden_iframe"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="first_name"
                    placeholder={t.hero.firstNamePlaceholder}
                    className="w-full bg-zinc-950/50 border border-zinc-800 rounded-xl px-5 py-4 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 transition-all text-sm"
                    required
                  />
                  <input
                    type="text"
                    name="last_name"
                    placeholder={t.hero.lastNamePlaceholder}
                    className="w-full bg-zinc-950/50 border border-zinc-800 rounded-xl px-5 py-4 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 transition-all text-sm"
                    required
                  />
                </div>

                <input
                  type="email"
                  name="email"
                  placeholder={t.hero.emailPlaceholder}
                  className="w-full bg-zinc-950/50 border border-zinc-800 rounded-xl px-5 py-4 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 transition-all text-sm"
                  required
                />

                <div style={{position: 'absolute', left: '-5000px'}} aria-hidden="true">
                  <input type="text" name="a_password" tabIndex="-1" defaultValue="" />
                </div>

                <div className="flex items-start gap-3 pt-2 text-left">
                  <div className="relative flex items-center mt-1">
                    <input
                      type="checkbox"
                      id="gdpr"
                      name="gdpr"
                      value="1"
                      required
                      className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-zinc-800 bg-zinc-950 transition-all checked:border-zinc-500 checked:bg-zinc-500 focus:outline-none"
                    />
                    <SafeIcon
                      icon={FiIcons.FiCheck}
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none text-xs"
                    />
                  </div>
                  <label htmlFor="gdpr" className="text-[11px] leading-relaxed text-zinc-500 cursor-pointer select-none">
                    {t.hero.gdprLabel}
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full bg-zinc-50 text-zinc-950 font-bold px-8 py-5 rounded-xl hover:bg-white transition-all transform hover:-translate-y-0.5 active:translate-y-0 shadow-xl shadow-black/20 text-sm tracking-widest uppercase mt-4"
                >
                  {t.hero.submitBtn}
                </button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{opacity: 0, scale: 0.9, y: 20}}
              animate={{opacity: 1, scale: 1, y: 0}}
              className="bg-zinc-900/40 border border-zinc-800/50 p-12 rounded-[2.5rem] backdrop-blur-md flex flex-col items-center gap-6"
            >
              <div className="w-16 h-16 rounded-full bg-zinc-50 flex items-center justify-center shadow-2xl shadow-zinc-50/10">
                <SafeIcon icon={FiCheckCircle} className="text-3xl text-zinc-950" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{t.hero.successTitle}</h2>
                <p className="text-zinc-400 font-light leading-relaxed">{t.hero.successMessage}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}